import { defineComponent, ref, computed, watch, createVNode, mergeProps } from "vue";
import NP from "number-precision";
import { getPrefixCls } from "../_utils/global-config.js";
import { isUndefined, isNumber } from "../_utils/is.js";
import IconUp from "../icon/icon-up/index.js";
import IconDown from "../icon/icon-down/index.js";
import IconPlus from "../icon/icon-plus/index.js";
import IconMinus from "../icon/icon-minus/index.js";
import Button from "../button/index.js";
import Input from "../input/index.js";
import { SIZES } from "../_utils/constant.js";
const InputGroup = Input.Group;
const MODES = ["embed", "button"];
const SPEED = 150;
NP.enableBoundaryChecking(false);
var _InputNumber = defineComponent({
  name: "InputNumber",
  inheritAttrs: false,
  props: {
    modelValue: Number,
    defaultValue: Number,
    mode: {
      type: String,
      default: "embed",
      validator: (value) => MODES.includes(value)
    },
    precision: Number,
    step: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    },
    max: {
      type: Number,
      default: Infinity
    },
    min: {
      type: Number,
      default: -Infinity
    },
    formatter: {
      type: Function
    },
    parser: {
      type: Function
    },
    placeholder: String,
    hideButton: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "medium",
      validator: (value) => {
        return SIZES.includes(value);
      }
    },
    onChange: {
      type: [Function, Array]
    },
    onFocus: {
      type: [Function, Array]
    },
    onBlur: {
      type: [Function, Array]
    }
  },
  emits: [
    "update:modelValue",
    "change",
    "focus",
    "blur"
  ],
  setup(props, {
    emit,
    attrs
  }) {
    var _a, _b;
    const prefixCls = getPrefixCls("input-number");
    const inputRef = ref();
    const _value = ref(isUndefined(props.defaultValue) ? "" : (_b = (_a = props.formatter) == null ? void 0 : _a.call(props, String(props.defaultValue))) != null ? _b : String(props.defaultValue));
    const numberPrefix = ref("");
    const mergedPrecision = computed(() => {
      if (isNumber(props.precision)) {
        const decimal = `${props.step}`.split(".")[1];
        const stepPrecision = decimal && decimal.length || 0;
        return Math.max(stepPrecision, props.precision);
      }
      return void 0;
    });
    const computedValue = computed(() => {
      var _a2, _b2;
      if (isNumber(props.modelValue) && props.modelValue !== Number(_value.value)) {
        return (_b2 = (_a2 = props.formatter) == null ? void 0 : _a2.call(props, String(props.modelValue))) != null ? _b2 : String(props.modelValue);
      }
      if (numberPrefix.value && !_value.value) {
        return numberPrefix.value;
      }
      return _value.value;
    });
    const isMax = ref(false);
    const isMin = ref(false);
    let repeatTimer = 0;
    const clearRepeatTimer = () => {
      if (repeatTimer) {
        window.clearTimeout(repeatTimer);
        repeatTimer = 0;
      }
    };
    const getLegalValue = (value) => {
      if (isUndefined(value) || value === "") {
        return void 0;
      }
      let numberValue = isNumber(value) ? value : Number(value);
      if (Number.isNaN(numberValue)) {
        return void 0;
      }
      if (isNumber(props.min) && numberValue < props.min) {
        numberValue = props.min;
      }
      if (isNumber(props.max) && numberValue > props.max) {
        numberValue = props.max;
      }
      return isNumber(mergedPrecision.value) ? NP.round(numberValue, mergedPrecision.value) : numberValue;
    };
    const finalValue = getLegalValue(computedValue.value);
    if (isNumber(finalValue)) {
      if (finalValue <= props.min) {
        isMin.value = true;
      }
      if (finalValue >= props.max) {
        isMax.value = true;
      }
    }
    const updateValue = (value, ev) => {
      const finalValue2 = getLegalValue(value);
      isMin.value = false;
      isMax.value = false;
      if (isNumber(finalValue2)) {
        if (finalValue2 <= props.min) {
          isMin.value = true;
        }
        if (finalValue2 >= props.max) {
          isMax.value = true;
        }
      }
      emit("update:modelValue", finalValue2);
      emit("change", finalValue2, ev);
    };
    const handleStepButton = (event, method, needRepeat = false) => {
      var _a2, _b2, _c, _d, _e;
      event.preventDefault();
      (_a2 = inputRef.value) == null ? void 0 : _a2.focus();
      if (props.disabled || method === "plus" && isMax.value || method === "minus" && isMin.value) {
        return;
      }
      let nextValue;
      if (!computedValue.value) {
        nextValue = props.min === -Infinity ? 0 : props.min;
      } else {
        const numberValue = Number((_c = (_b2 = props.parser) == null ? void 0 : _b2.call(props, computedValue.value)) != null ? _c : computedValue.value);
        nextValue = NP[method](numberValue, props.step);
      }
      _value.value = (_e = (_d = props.formatter) == null ? void 0 : _d.call(props, String(nextValue))) != null ? _e : String(nextValue);
      updateValue(nextValue, event);
      if (needRepeat) {
        repeatTimer = window.setTimeout(() => event.target.dispatchEvent(event), SPEED);
      }
    };
    const handleInput = (value, ev) => {
      var _a2, _b2, _c, _d;
      value = value.trim().replace(/。/g, ".");
      value = (_b2 = (_a2 = props.parser) == null ? void 0 : _a2.call(props, value)) != null ? _b2 : value;
      if (isNumber(Number(value)) || /^\.|-$/.test(value)) {
        if (/^\.|-$/.test(value)) {
          numberPrefix.value = value;
        } else if (numberPrefix.value) {
          numberPrefix.value = "";
        }
        _value.value = (_d = (_c = props.formatter) == null ? void 0 : _c.call(props, value)) != null ? _d : value;
        updateValue(value, ev);
      }
    };
    const handleFocus = (ev) => {
      emit("focus", ev);
    };
    const handleBlur = (ev) => {
      var _a2, _b2, _c, _d;
      if (computedValue.value) {
        const numberValue = Number((_b2 = (_a2 = props.parser) == null ? void 0 : _a2.call(props, computedValue.value)) != null ? _b2 : computedValue.value);
        const finalValue2 = getLegalValue(numberValue);
        if (finalValue2 !== numberValue) {
          _value.value = (_d = (_c = props.formatter) == null ? void 0 : _c.call(props, String(finalValue2))) != null ? _d : String(finalValue2);
          updateValue(finalValue2, ev);
        }
      }
      emit("blur", ev);
    };
    watch(() => props.modelValue, (value) => {
      var _a2, _b2;
      let stringValue = isUndefined(value) ? "" : String(value);
      stringValue = (_b2 = (_a2 = props.formatter) == null ? void 0 : _a2.call(props, stringValue)) != null ? _b2 : stringValue;
      if (stringValue !== _value.value) {
        _value.value = stringValue;
      }
    });
    const renderSuffix = () => createVNode("div", {
      "class": `${prefixCls}-step`
    }, [createVNode("button", {
      "class": [`${prefixCls}-step-button`, {
        [`${prefixCls}-step-button-disabled`]: props.disabled || isMax.value
      }],
      "type": "button",
      "disabled": props.disabled || isMax.value,
      "onMousedown": (e) => handleStepButton(e, "plus", true),
      "onMouseup": clearRepeatTimer,
      "onMouseleave": clearRepeatTimer
    }, [createVNode(IconUp, null, null)]), createVNode("button", {
      "class": [`${prefixCls}-step-button`, {
        [`${prefixCls}-step-button-disabled`]: props.disabled || isMin.value
      }],
      "type": "button",
      "disabled": props.disabled || isMin.value,
      "onMousedown": (e) => handleStepButton(e, "minus", true),
      "onMouseup": clearRepeatTimer,
      "onMouseleave": clearRepeatTimer
    }, [createVNode(IconDown, null, null)])]);
    const cls = computed(() => [prefixCls, `${prefixCls}-mode-${props.mode}`, `${prefixCls}-size-${props.size}`]);
    const renderInput = () => {
      const inputSlots = props.mode === "embed" && !props.hideButton ? {
        suffix: renderSuffix
      } : {};
      return createVNode(Input, mergeProps(attrs, {
        "ref": inputRef,
        "class": cls.value,
        "type": "text",
        "size": props.size,
        "modelValue": computedValue.value,
        "placeholder": props.placeholder,
        "disabled": props.disabled,
        "onInput": handleInput,
        "onFocus": handleFocus,
        "onBlur": handleBlur
      }), inputSlots);
    };
    const render = () => {
      if (props.mode === "embed") {
        return renderInput();
      }
      return createVNode(InputGroup, null, {
        default: () => [createVNode(Button, {
          "size": props.size,
          "disabled": props.disabled || isMin.value,
          "onMousedown": (e) => handleStepButton(e, "minus", true),
          "onMouseup": clearRepeatTimer,
          "onMouseleave": clearRepeatTimer
        }, {
          icon: () => createVNode(IconMinus, null, null)
        }), renderInput(), createVNode(Button, {
          "size": props.size,
          "disabled": props.disabled || isMax.value,
          "onMousedown": (e) => handleStepButton(e, "plus", true),
          "onMouseup": clearRepeatTimer,
          "onMouseleave": clearRepeatTimer
        }, {
          icon: () => createVNode(IconPlus, null, null)
        })]
      });
    };
    return {
      inputRef,
      render
    };
  },
  methods: {
    focus() {
      var _a;
      (_a = this.inputRef) == null ? void 0 : _a.focus();
    },
    blur() {
      var _a;
      (_a = this.inputRef) == null ? void 0 : _a.blur();
    }
  },
  render() {
    return this.render();
  }
});
export { _InputNumber as default };
